import React, { createContext, useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import qs from 'qs';

import { isEqual } from 'lodash';
import PageNotFound from '../../404';
import SubscriptionsTable from './components/Table';
import SubscriptionsMainHeader from './components/Header';
import Modals from './components/Modals';

import {
  changePage,
  listSuccess,
  loadingTable,
  subscriptionsMainReducer,
  subscriptionsMainState,
  updateListError,
  updateListSuccess,
  loadingTablePagination,
  updatePaginationSuccess,
} from './reducer';
import { getErrorMessage, pageWrapperScrollTop } from '../../../utils/helper';
import { subscriptionsConfiguration, subscriptionsList, subscriptionsListPagination } from '../../../services/endpoints/subscriptions';
import { statusesCheck } from './helpers';
import Loader from '../../../components/Loaders/Loader';

export const SubscriptionsMainContext = createContext({});
const Subscriptions = () => {
  const [subscriptionsStore, subscriptionsDispatch] = useReducer(subscriptionsMainReducer, subscriptionsMainState);
  const { isInitPage, initPageError, updateList, pagination } = subscriptionsStore;

  const location = useLocation();
  const queryString = qs.parse(location.search.slice(1));

  const [urlChangesDebounce] = useDebounce(location.search, 350);

  const updateListing = async () => {
    try {
      const response = await subscriptionsList(queryString);
      const isUpdatePagination = !isEqual(response.data?.filters, subscriptionsStore?.filters)
      const payload = {
        ...response.data,
        pagination: isUpdatePagination ? response.data.pagination : {
          ...subscriptionsStore.pagination,
          current_page: response.data.pagination.current_page,
        },
      };
      subscriptionsDispatch(updateListSuccess(payload));
      /**
       * 1 - Проверка, нужен ли запрос пагинации
       */
      if (response.data.need_pagination && isUpdatePagination) {
        subscriptionsDispatch(loadingTablePagination());
        const responsePagination = await subscriptionsListPagination(queryString);
        const pagination = responsePagination.data.pagination;
        subscriptionsDispatch(updatePaginationSuccess(pagination));
      }
    } catch (error) {
      const data = getErrorMessage(error);
      const status = error?.status ? error.status : 500;
      const statusText = error?.statusText ? error.statusText : 'Unknown error';
      subscriptionsDispatch(updateListError(data, status, statusText));
    }
  };
  const initPage = async () => {
    try {
      /**
       * 1 - Проверка доступтов в запросе config
       */
      const configuration = await subscriptionsConfiguration();
      const access = configuration?.data?.access;
      if (!access || !access.list) {
        throw new Error('Error access');
      }
      const requestPull = [await subscriptionsList(queryString)];

      const [respList] = await Promise.all(requestPull);
      const payload = {
        configuration: {
          access: configuration.data.access,
        },
        delays: configuration.data.delays,
        search_types: configuration.data.search_types,
        services: configuration.data.services,
        statuses: configuration.data.statuses,
        ...respList.data,
      };

      /**
       * 2 - Проверка ID статусов
       */
      const isStatusCorrect = statusesCheck(configuration.data.statuses);
      if (!isStatusCorrect) {
        throw new Error('Conflict statuses');
      }
      subscriptionsDispatch(listSuccess(payload));

      /**
       * 3 - Проверка, нужен ли запрос пагинации
       */
      if (respList.data.need_pagination) {
        subscriptionsDispatch(loadingTablePagination());
        const responsePagination = await subscriptionsListPagination(queryString);
        const pagination = responsePagination.data.pagination;
        subscriptionsDispatch(updatePaginationSuccess(pagination));
      }
    } catch (error) {
      const data = getErrorMessage(error);
      const status = error.status ? error.status : 500;
      const statusText = error.statusText ? error.statusText : 'Unknown error';
      subscriptionsDispatch(updateListError(data, status, statusText));
    }
  };

  useEffect(() => {
    if (!isInitPage) {
      initPage();
    }
    document.title = 'Subscriptions';

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitPage) {
      subscriptionsDispatch(loadingTable());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (isInitPage) {
      subscriptionsDispatch(loadingTable());
      updateListing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChangesDebounce]);

  useEffect(() => {
    if (updateList) {
      updateListing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateList]);

  useEffect(() => {
    if (isInitPage && queryString.page) {
      const newPage = queryString.page ? queryString.page : pagination.current_page;
      subscriptionsDispatch(changePage(newPage));
      pageWrapperScrollTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString.page]);

  if (!isInitPage) {
    return <Loader />;
  }

  if (isInitPage && initPageError) {
    return <PageNotFound errorDataMessage={initPageError?.data} errorStatus={initPageError?.status} errorStatusText={initPageError?.statusText} />;
  }

  return (
    <SubscriptionsMainContext.Provider value={[subscriptionsStore, subscriptionsDispatch]}>
      <div>
        <SubscriptionsMainHeader />
        <SubscriptionsTable />
      </div>
      <Modals />
    </SubscriptionsMainContext.Provider>
  );
};

export default Subscriptions;
